import {loggerFactory} from "@cm/browser-log"
import {
    IConnectionSolver,
    IOptimizationEntity,
    IRelation,
    IResidualBlock,
    IRigidBody,
    IVariable,
    IVariable_R1,
    IVariable_S1,
    IVariable_S3,
    Matrix,
    ObjectId,
    RotationalFreedomMode,
    RotationalJoint,
    TranslationalJoint,
    VariableId,
    VariableType,
    Vector,
} from "@cm/template-nodes"
import {Matrix4, Vector3, Quaternion} from "@cm/math"
import {TransformParameters3D} from "@app/template-editor/helpers/connection-solver/parameter-blocks"
import {toMatrix, angleDegreesToQuaternion, quaternionToAngleDegrees} from "@common/helpers/utils/math-utils"
import {Solver} from "@app/template-editor/helpers/connection-solver/least-squares-solver"

function angleDegreesToSphere1(angle: number) {
    angle *= Math.PI / 180
    return [Math.sin(angle), Math.cos(angle)] as const
}

function sphere1ToAngleDegrees(values: [number, number]) {
    return Math.atan2(values[0], values[1]) * (180 / Math.PI)
}

enum ObjectState {
    Active,
    Inactive,
    InactiveUntilConvergenceReached,
    CurrentlyMovedByUserInitiallyActive,
    CurrentlyMovedByUserInitiallyInactive,
}

class Variable_R1 implements IVariable_R1 {
    type = "boundedReal" as const
    relation!: IRelation
    property!: "tx" | "ty" | "tz"
    range!: [number, number]
    default!: number
    value!: number
    holdDefault = true
}

class Variable_S1 implements IVariable_S1 {
    type = "1-sphere" as const
    relation!: IRelation
    property!: "rx" | "ry" | "rz"
    default!: [number, number]
    value!: [number, number]
    holdDefault = true
}

class Variable_S3 implements IVariable_S3 {
    type = "3-sphere" as const
    relation!: IRelation
    property!: "r"
    default!: [number, number, number, number]
    value!: [number, number, number, number]
    holdDefault = true
}

function clamp(x: number, a: number, b: number) {
    if (x < a) return a
    else if (x > b) return b
    else return x
}

function d_clamp(x: number, a: number, b: number) {
    if (x < a) return 0
    else if (x > b) return 0
    else return 1
}

export class ObjectRelation implements IRelation {
    transform: TransformParameters3D = new TransformParameters3D()
    readonly transformLimits = {tx: [0, 0], ty: [0, 0], tz: [0, 0]}

    postConverge: undefined | (() => boolean) = undefined

    readonly residualBlocks: IResidualBlock[]

    constructor(
        readonly objectA: ObjectData,
        readonly objectB: ObjectData,
    ) {
        this.transform.tx_free = false
        this.transform.ty_free = false
        this.transform.tz_free = false
        this.transform.r_free = RotationalFreedomMode.None

        this.residualBlocks = [
            {
                type: "linear",
                size: 4 * 4,
                active: true,
                relatedParameters: [this.objectA.transform, this.objectB.transform, this.transform],
                evaluate: (residuals: Vector, jacobians: Matrix[]) => {
                    const matrixAInv = this.objectA.transform.matrixInv
                    const dMatrixA_dTransformA = this.objectA.transform.jacobian
                    const dMatrixAInv_dTransformA = this.objectA.transform.jacobianInv
                    const matrixB = this.objectB.transform.matrix
                    const dMatrixB_dTransformB = this.objectB.transform.jacobian
                    const jacobianA = jacobians[0]
                    const jacobianB = jacobians[1]
                    const jacobianT = jacobians[2]

                    const transform = this.transform.matrix
                    const dTransform_dParamsT = this.transform.jacobian

                    //residuals
                    for (let k = 0; k < 4; k++) {
                        for (let i = 0; i < 4; i++) {
                            let tmp = 0
                            for (let j = 0; j < 4; j++) {
                                tmp += matrixAInv.get(k, j) * matrixB.get(j, i)
                            }
                            residuals.set(k * 4 + i, tmp - transform.get(k, i))
                        }
                    }

                    // jacobian
                    const nParams = dMatrixA_dTransformA.shape[0]
                    for (let p = 0; p < nParams; p++) {
                        for (let k = 0; k < 4; k++) {
                            for (let i = 0; i < 4; i++) {
                                let tmpA = 0
                                let tmpB = 0
                                for (let j = 0; j < 4; j++) {
                                    tmpA += dMatrixAInv_dTransformA.get(p, k, j) * matrixB.get(j, i)
                                    tmpB += matrixAInv.get(k, j) * dMatrixB_dTransformB.get(p, j, i)
                                }
                                jacobianA.set(k * 4 + i, p, tmpA)
                                jacobianB.set(k * 4 + i, p, tmpB)
                                jacobianT.set(k * 4 + i, p, -dTransform_dParamsT.get(p, k, i))
                            }
                        }
                    }
                    return true
                },
            },
            {
                // Transform constraints
                type: "linear",
                size: 3,
                active: true,
                relatedParameters: [this.transform],
                evaluate: (residuals: Vector, jacobians: Matrix[]) => {
                    const J = jacobians[0]
                    const p = this.transform
                    const [x0, x1] = this.transformLimits.tx
                    const [y0, y1] = this.transformLimits.ty
                    const [z0, z1] = this.transformLimits.tz
                    if (p.tx_free) {
                        const px = p.tx
                        residuals.set(0, px - clamp(px, x0, x1))
                        J.set(0, 0, 1 - d_clamp(px, x0, x1))
                    }
                    if (p.ty_free) {
                        const py = p.ty
                        residuals.set(1, py - clamp(py, y0, y1))
                        J.set(1, 1, 1 - d_clamp(py, y0, y1))
                    }
                    if (p.tz_free) {
                        const pz = p.tz
                        residuals.set(2, pz - clamp(pz, z0, z1))
                        J.set(2, 2, 1 - d_clamp(pz, z0, z1))
                    }
                    return true
                },
            },
        ]
    }

    update() {
        this.transform.updateMatrixAndJacobian()
        this.residualBlocks[1].active = this.transform.tx_free || this.transform.ty_free || this.transform.tz_free
    }
}

/** A weak constraint that tries to move a point in object space to a fixed location. Useful for real-time manipulation of an object */
class PointTarget implements IOptimizationEntity {
    public weight = 0.01

    private projectionMatrix!: Matrix
    private objPoint = [0, 0, 0, 1]
    private targetX = 0
    private targetY = 0
    readonly residualBlocks: IResidualBlock[]

    constructor(readonly object: IRigidBody) {
        this.residualBlocks = [
            {
                type: "linear",
                size: 2,
                active: true,
                relatedParameters: [this.object.transform],
                evaluate: (residuals: Vector, jacobians: Matrix[]) => {
                    const projMatrix = this.projectionMatrix
                    const objMatrix = this.object.transform.matrix
                    const dObjMatrix_dParams = this.object.transform.jacobian
                    const objPoint = this.objPoint
                    const worldPoint = [0, 0, 0, 1]
                    const projPoint = [0, 0, 0, 1]

                    // object space to world space
                    for (let i = 0; i < 3; i++) {
                        let tmp = 0
                        for (let j = 0; j < 4; j++) {
                            tmp += objMatrix.get(i, j) * objPoint[j]
                        }
                        worldPoint[i] = tmp
                    }

                    // world space to projective camera space
                    for (let i = 0; i < 4; i++) {
                        let tmp = 0
                        for (let j = 0; j < 4; j++) {
                            tmp += projMatrix.get(i, j) * worldPoint[j]
                        }
                        projPoint[i] = tmp
                    }

                    // project camera space to screen (see THREEjs Vector3.project / applyMatrix4)
                    const screenX = projPoint[0]
                    const screenY = projPoint[1]
                    residuals.set(0, (screenX - this.targetX * projPoint[3]) * this.weight)
                    residuals.set(1, (screenY - this.targetY * projPoint[3]) * this.weight)

                    // dScreen_dParams = dScreen_dProjPoint * dProjPoint_dWorldPoint * dWorldPoint_dObjMatrix * dObjMatrix_dParams
                    const dScreenX_dProjPoint = [1, 0, 0, 0]
                    const dScreenY_dProjPoint = [0, 1, 0, 0]
                    const nParams = dObjMatrix_dParams.shape[0]
                    for (let p = 0; p < nParams; p++) {
                        const dWorldPoint = [0, 0, 0, 0]
                        for (let i = 0; i < 4; i++) {
                            let tmp = 0
                            for (let j = 0; j < 4; j++) {
                                tmp += dObjMatrix_dParams.get(p, i, j) * objPoint[j]
                            }
                            dWorldPoint[i] = tmp
                        }

                        const dProjPoint = [0, 0, 0, 0]
                        for (let i = 0; i < 4; i++) {
                            let tmp = 0
                            for (let j = 0; j < 4; j++) {
                                tmp += projMatrix.get(i, j) * dWorldPoint[j]
                            }
                            dProjPoint[i] = tmp
                        }

                        let jacobianX = 0
                        let jacobianY = 0
                        for (let i = 0; i < 4; i++) {
                            jacobianX += dProjPoint[i] * dScreenX_dProjPoint[i]
                            jacobianY += dProjPoint[i] * dScreenY_dProjPoint[i]
                        }

                        jacobianX -= this.targetX * dProjPoint[3]
                        jacobianY -= this.targetY * dProjPoint[3]
                        jacobians[0].set(0, p, jacobianX * this.weight)
                        jacobians[0].set(1, p, jacobianY * this.weight)
                    }
                    return true
                },
            },
        ]
    }

    setProjectionMatrix(projectionMatrix: Matrix4): void {
        this.projectionMatrix = toMatrix(projectionMatrix.toArrayTransposed(), 4, 4)
    }

    setObjPoint(objPoint: Vector3): void {
        this.objPoint[0] = objPoint.x
        this.objPoint[1] = objPoint.y
        this.objPoint[2] = objPoint.z
    }

    setTarget(targetX: number, targetY: number): void {
        this.targetX = targetX
        this.targetY = targetY
    }
}

class ObjectData implements IRigidBody {
    transform = new TransformParameters3D()
    optimizationEntities = new Set<IOptimizationEntity>()
    state: ObjectState = ObjectState.Active
}

export class ConnectionSolver implements IConnectionSolver {
    private solver = new Solver()
    private objects = new Map<ObjectId, ObjectData>()
    private optimizationEntities = new Set<IOptimizationEntity>()
    private iterationsPerStep = 10
    private defaultUpdateStepCount = 30
    private stepsRemaining = 30

    constructor() {
        this.solver.onPreEval = () => {
            for (const entity of this.optimizationEntities) {
                if (entity.preEval) entity.preEval()
            }
        }
    }

    destroy() {
        this.solver.destroy()
    }

    private addEntity(entity: IOptimizationEntity): void {
        entity.residualBlocks.forEach((r) => this.solver.addResidualBlock(r))
        this.optimizationEntities.add(entity)
    }

    private removeEntity(entity: IOptimizationEntity): void {
        entity.residualBlocks.forEach((r) => this.solver.removeResidualBlock(r))
        this.optimizationEntities.delete(entity)
    }

    public addObject(objId: ObjectId): IRigidBody {
        const objData = new ObjectData()
        this.objects.set(objId, objData)
        this.triggerUpdate()
        return objData
    }

    public removeObject(objId: ObjectId): void {
        const objData = this.objects.get(objId)
        if (!objData) {
            return
        }
        this.objects.delete(objId)
        objData.optimizationEntities.forEach((entity) => this.removeEntity(entity))
        this.triggerUpdate()
    }

    private _setObjectMatrix(obj: ObjectData, matrix: Matrix4): void {
        const {position, quaternion} = matrix.decompose()
        obj.transform.tx = position.x
        obj.transform.ty = position.y
        obj.transform.tz = position.z
        obj.transform.qx = quaternion.x
        obj.transform.qy = quaternion.y
        obj.transform.qz = quaternion.z
        obj.transform.qw = quaternion.w
        obj.transform.updateMatrixAndJacobian()
        //TODO: update residual block flags?
        this.triggerUpdate()
    }

    public setObjectMatrix(objId: ObjectId, matrix: Matrix4): void {
        const objData = this.objects.get(objId)
        if (!objData) {
            ConnectionSolver.logger?.warn(`Object ${objId} not found`)
            return
        }
        this._setObjectMatrix(objData, matrix)
    }

    public setObjectActive(objId: ObjectId, translationActive: boolean, rotationActive?: boolean, force?: boolean): void {
        const objData = this.objects.get(objId)
        if (!objData) {
            ConnectionSolver.logger.warn(`Object ${objId} not found`)
            return
        }

        if (force === undefined) {
            force = false
        }

        if (objData.state === ObjectState.InactiveUntilConvergenceReached && !force) {
            return
        }

        if (rotationActive === undefined) {
            rotationActive = translationActive
        }

        objData.state = translationActive || rotationActive ? ObjectState.Active : ObjectState.Inactive
        this.internalSetObjectActive(objData, translationActive, rotationActive)
    }

    public setObjectBeingMoved(objId: ObjectId): void {
        const objData = this.objects.get(objId)
        if (!objData) {
            ConnectionSolver.logger.warn(`Object ${objId} not found`)
            return
        }

        const oldState = objData.state
        if (oldState === ObjectState.Active || oldState === ObjectState.InactiveUntilConvergenceReached) {
            objData.state = ObjectState.CurrentlyMovedByUserInitiallyActive
        } else if (oldState === ObjectState.Inactive) {
            objData.state = ObjectState.CurrentlyMovedByUserInitiallyInactive
        }

        if (oldState !== objData.state) {
            this.internalSetObjectActive(objData, false, false)
        }
    }

    public setObjectReleased(objId: ObjectId): void {
        const objData = this.objects.get(objId)
        if (!objData) {
            ConnectionSolver.logger.warn(`Object ${objId} not found`)
            return
        }

        if (objData.state === ObjectState.CurrentlyMovedByUserInitiallyActive) {
            objData.state = ObjectState.InactiveUntilConvergenceReached
        } else if (objData.state === ObjectState.CurrentlyMovedByUserInitiallyInactive) {
            objData.state = ObjectState.Inactive
        }
    }

    public setObjectInstantMove(objId: ObjectId): void {
        const objData = this.objects.get(objId)
        if (!objData) {
            ConnectionSolver.logger.warn(`Object ${objId} not found`)
            return
        }

        if (objData.state === ObjectState.Active || objData.state === ObjectState.CurrentlyMovedByUserInitiallyActive) {
            objData.state = ObjectState.InactiveUntilConvergenceReached
            this.internalSetObjectActive(objData, false, false)
        }
    }

    private updateObjectsStates(): void {
        for (const obj of this.objects) {
            const objData = obj[1]
            if (objData.state === ObjectState.InactiveUntilConvergenceReached) {
                objData.state = ObjectState.Active
                this.internalSetObjectActive(objData, true, true)
            }
        }
    }

    private runPostConvergeCallbacks(): void {
        let reTriggerUpdate = false
        for (const entity of this.optimizationEntities) {
            if (entity.postConverge) {
                if (entity.postConverge()) {
                    reTriggerUpdate = true
                }
            }
        }
        if (reTriggerUpdate) {
            this.triggerUpdate()
        }
    }

    private internalSetObjectActive(objData: ObjectData, translationActive: boolean, rotationActive: boolean): void {
        let didChange = false
        if (translationActive !== objData.transform.tx_free) {
            objData.transform.tx_free = translationActive
            objData.transform.ty_free = translationActive
            objData.transform.tz_free = translationActive
            didChange = true
        }
        if (rotationActive !== (objData.transform.r_free === RotationalFreedomMode.Full)) {
            objData.transform.r_free = rotationActive ? RotationalFreedomMode.Full : RotationalFreedomMode.None
            didChange = true
        }
        if (didChange) {
            objData.transform.updateMatrixAndJacobian()
            this.triggerUpdate()
        }
    }

    public getObjectMatrix(objId: ObjectId): Matrix4 | null {
        const objData = this.objects.get(objId)
        if (!objData) {
            return null
        }
        return Matrix4.fromArrayTransposed(objData.transform.matrix.data as ArrayLike<number>)
    }

    public addVariable(varId: VariableId, type: VariableType): IVariable {
        switch (type) {
            case "boundedReal":
                return new Variable_R1()
            case "1-sphere":
                return new Variable_S1()
            case "3-sphere":
                return new Variable_S3()
        }
    }

    public removeVariable(variable: IVariable): void {
        const transform = variable.relation?.transform
        if (transform) {
            transform.tx_free = false
            transform.ty_free = false
            transform.tz_free = false
            transform.r_free = RotationalFreedomMode.None
        }
    }

    public getVariableValue(variable: IVariable) {
        const transform = variable.relation?.transform
        if (transform) {
            const getAngles = () => quaternionToAngleDegrees(new Quaternion(transform.qx, transform.qy, transform.qz, transform.qw))
            switch (variable.property) {
                case "tx":
                    return transform.tx * 2
                case "ty":
                    return transform.ty * 2
                case "tz":
                    return transform.tz * 2
                case "rx":
                    return angleDegreesToSphere1(getAngles()[0])
                case "ry":
                    return angleDegreesToSphere1(getAngles()[1])
                case "rz":
                    return angleDegreesToSphere1(getAngles()[2])
                case "r": {
                    const q = new Quaternion(transform.qx, transform.qy, transform.qz, transform.qw)
                    return [q.x, q.y, q.z, q.w] as const
                }
            }
        } else {
            return undefined
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public setVariableValue(variable: IVariable, value: any, triggerUpdate = true) {
        const transform = variable.relation?.transform
        variable.value = value
        if (transform) {
            const setAngles = (rx: number, ry: number, rz: number) => {
                const q = angleDegreesToQuaternion(rx, ry, rz)
                transform.qx = q.x
                transform.qy = q.y
                transform.qz = q.z
                transform.qw = q.w
            }
            switch (variable.property) {
                case "tx":
                    transform.tx = value ?? 0
                    break
                case "ty":
                    transform.ty = value ?? 0
                    break
                case "tz":
                    transform.tz = value ?? 0
                    break
                case "rx":
                    setAngles(sphere1ToAngleDegrees(value ?? 0), 0, 0)
                    break
                case "ry":
                    setAngles(0, sphere1ToAngleDegrees(value ?? 0), 0)
                    break
                case "rz":
                    setAngles(0, 0, sphere1ToAngleDegrees(value ?? 0))
                    break
                case "r":
                    {
                        value ??= [0, 0, 0, 1]
                        const q = new Quaternion(value[0], value[1], value[2], value[3])
                        transform.qx = q.x
                        transform.qy = q.y
                        transform.qz = q.z
                        transform.qw = q.w
                    }
                    break
            }
            variable.relation.update()
            if (triggerUpdate) {
                this.triggerUpdate()
            }
        }
    }

    public setVariableDefault(
        variable: IVariable,
        defaultValue: number | [number, number] | [number, number, number, number],
        holdUntilConvergence: boolean,
        triggerUpdate = true,
    ) {
        variable.default = defaultValue
        if (holdUntilConvergence) {
            variable.holdDefault = true
            // console.log("Holding defaults for", variable);
        }
        this.setVariableValue(variable, defaultValue, triggerUpdate)
    }

    public setVariableConstraints(variable: IVariable, range: [number, number], triggerUpdate = true): void {
        if (variable.type === "boundedReal") {
            variable.range = range

            const transform = variable.relation?.transform
            if (transform) {
                const min = Math.min(...range)
                const max = Math.max(...range)
                transform[variable.property] = clamp(transform[variable.property], min, max)
                variable.relation.transformLimits[variable.property] = [min, max]
                variable.relation.update()
                if (triggerUpdate) {
                    this.triggerUpdate()
                }
            }
        }
    }

    public addObjectRelation(objIdA: ObjectId, objIdB: ObjectId) {
        const objA = this.objects.get(objIdA)
        const objB = this.objects.get(objIdB)
        if (!objA) {
            ConnectionSolver.logger.error("Could not find object:", objIdA)
            return undefined
        }
        if (!objB) {
            ConnectionSolver.logger.error("Could not find object:", objIdB)
            return undefined
        }
        const relation = new ObjectRelation(objA, objB)
        this.addEntity(relation)
        objA.optimizationEntities.add(relation)
        objB.optimizationEntities.add(relation)
        this.triggerUpdate()
        return relation
    }

    public removeRelation(relation: IRelation): void {
        ;(relation.objectA as ObjectData).optimizationEntities.delete(relation)
        ;(relation.objectB as ObjectData).optimizationEntities.delete(relation)
        this.removeEntity(relation)
        this.triggerUpdate()
    }

    public setRelationConstraints(relation: IRelation, trans: TranslationalJoint, rot: RotationalJoint, triggerUpdate = true) {
        const transform = relation.transform

        const holdClearList: IVariable[] = []

        const setTrans = (key: "tx" | "ty" | "tz", freeKey: "tx_free" | "ty_free" | "tz_free") => {
            const t = trans[key]
            if (t instanceof Variable_R1) {
                t.relation = relation
                t.property = key
                if (t.holdDefault) {
                    transform[freeKey] = false
                    holdClearList.push(t)
                } else {
                    transform[freeKey] = true
                }
                this.setVariableConstraints(t, t.range, triggerUpdate)
                this.setVariableDefault(t, t.default, false, triggerUpdate)
            } else if (typeof t === "number") {
                transform[freeKey] = false
                transform[key] = t
                relation.transformLimits[key] = [t, t]
            }
        }

        setTrans("tx", "tx_free")
        setTrans("ty", "ty_free")
        setTrans("tz", "tz_free")

        if (rot.type === "fixed") {
            transform.r_free = RotationalFreedomMode.None
            const q = angleDegreesToQuaternion(rot.rx, rot.ry, rot.rz)
            transform.qx = q.x
            transform.qy = q.y
            transform.qz = q.z
            transform.qw = q.w
        } else if (rot.type === "hinge") {
            rot.angleVariable.relation = relation

            if (rot.axis === "x") {
                transform.r_free = RotationalFreedomMode.X_Only
                rot.angleVariable.property = "rx"
            } else if (rot.axis === "y") {
                transform.r_free = RotationalFreedomMode.Y_Only
                rot.angleVariable.property = "ry"
            } else if (rot.axis === "z") {
                transform.r_free = RotationalFreedomMode.Z_Only
                rot.angleVariable.property = "rz"
            }

            if (rot.angleVariable.holdDefault) {
                holdClearList.push(rot.angleVariable)
                transform.r_free = RotationalFreedomMode.None
            }

            this.setVariableDefault(rot.angleVariable, rot.angleVariable.default, false, triggerUpdate)

            //TODO: hinge angle constraint (see http://twvideo01.ubm-us.net/o1/vault/gdc2016/Presentations/VanDenBergen_Gino_Rotational_Joint_Limits.pdf)
        } else if (rot.type === "ball") {
            rot.angleVariable.relation = relation
            rot.angleVariable.property = "r"

            transform.r_free = RotationalFreedomMode.Full
            if (rot.angleVariable.holdDefault) {
                holdClearList.push(rot.angleVariable)
                transform.r_free = RotationalFreedomMode.None
            }

            this.setVariableDefault(rot.angleVariable, rot.angleVariable.default, false, triggerUpdate)
        } else {
            ConnectionSolver.logger.error("Invalid rotation type:", rot)
        }

        relation.postConverge = () => {
            let didClear = false
            for (const variable of holdClearList) {
                variable.holdDefault = false
                // console.log("Clearing hold of defaults for", variable);
                didClear = true
            }
            if (didClear) {
                this.setRelationConstraints(relation, trans, rot, false)
            }
            return false
        }

        relation.update()

        if (triggerUpdate) {
            this.triggerUpdate()
        }
    }

    public setFixedRelationWithMatrix(relation: ObjectRelation, matrix: Matrix4) {
        const transform = relation.transform
        const {position, quaternion} = matrix.decompose()

        transform.tx_free = false
        transform.ty_free = false
        transform.tz_free = false
        transform.r_free = RotationalFreedomMode.None
        transform.tx = position.x
        transform.ty = position.y
        transform.tz = position.z
        transform.qx = quaternion.x
        transform.qy = quaternion.y
        transform.qz = quaternion.z
        transform.qw = quaternion.w
        relation.update()
        this.triggerUpdate()
    }

    public relationHasOpenDegreesOfFreedom(relation: IRelation): boolean {
        return relation.transform.hasFreeParams()
    }

    public getCurrentTransformationForRelation(relation: IRelation): number[] {
        const transform = relation.transform
        const q = new Quaternion(transform.qx, transform.qy, transform.qz, transform.qw)
        return [transform.tx, transform.ty, transform.tz, ...quaternionToAngleDegrees(q)]
    }

    public addPointTarget(objId: ObjectId, objPoint: Vector3, projectionMatrix: Matrix4): PointTarget {
        const obj = this.objects.get(objId)!
        const targ = new PointTarget(obj)
        targ.setObjPoint(objPoint)
        targ.setProjectionMatrix(projectionMatrix)
        this.addEntity(targ)
        obj.optimizationEntities.add(targ)
        this.triggerUpdate()
        return targ
    }

    public removePointTarget(targ: PointTarget): void {
        ;(targ.object as ObjectData).optimizationEntities.delete(targ)
        this.removeEntity(targ)
        this.triggerUpdate()
    }

    public setPointTarget(entity: PointTarget, targetX: number, targetY: number) {
        entity.setTarget(targetX, targetY)
        this.triggerUpdate()
    }

    public removeAll(): void {
        this.objects.forEach((objData) => {
            objData.optimizationEntities.forEach((entity) => this.removeEntity(entity))
        })
        this.objects.clear()
        this.triggerUpdate()
    }

    private buildRelationMap(): Map<ObjectData, [ObjectData, IRelation][]> {
        const relMap = new Map<ObjectData, [ObjectData, IRelation][]>()
        const addEntry = (a: ObjectData, b: ObjectData, r: IRelation) => {
            // symmetric map (add two entries: a->b and b->a)
            let list = relMap.get(a)
            if (!list) {
                list = []
                relMap.set(a, list)
            }
            list.push([b, r])
            list = relMap.get(b)
            if (!list) {
                list = []
                relMap.set(b, list)
            }
            list.push([a, r])
        }
        for (const entity of this.optimizationEntities) {
            if (entity instanceof ObjectRelation) {
                addEntry(entity.objectA, entity.objectB, entity)
            }
        }
        return relMap
    }

    public propagateFixedRelations(rootIds: ObjectId[]) {
        const relMap = this.buildRelationMap()
        const visitedSet = new Set<ObjectData>()
        const traverse = (fromObj: ObjectData, prevObj: ObjectData | null, depth: number) => {
            if (visitedSet.has(fromObj)) {
                ConnectionSolver.logger.warn("Object has multiple or circular constraints with no open degrees of freedom:", fromObj)
                return
            }
            visitedSet.add(fromObj)
            const entries = relMap.get(fromObj)
            if (!entries) return
            for (const [toObj, relation] of entries) {
                if (toObj === prevObj) {
                    continue // relation map is symmetric, so don't follow links to the immediate predecessor
                } else if (!this.relationHasOpenDegreesOfFreedom(relation)) {
                    if (relation instanceof ObjectRelation) {
                        if (fromObj === relation.objectA) {
                            // from A to B: B = A T
                            const matrixA = Matrix4.fromArrayTransposed(relation.objectA.transform.matrix.data as ArrayLike<number>)
                            const transform = Matrix4.fromArrayTransposed(relation.transform.matrix.data as ArrayLike<number>)
                            this._setObjectMatrix(toObj, matrixA.multiply(transform))
                        } else if (fromObj === relation.objectB) {
                            // from B to A: A = B T^-1
                            const matrixB = Matrix4.fromArrayTransposed(relation.objectB.transform.matrix.data as ArrayLike<number>)
                            const transformInv = Matrix4.fromArrayTransposed(relation.transform.matrixInv.data as ArrayLike<number>)
                            this._setObjectMatrix(toObj, matrixB.multiply(transformInv))
                        }
                        traverse(toObj, fromObj, depth + 1)
                    } else {
                        //TODO: other relation types
                    }
                }
            }
        }
        for (const rootId of rootIds) {
            const rootObj = this.objects.get(rootId)
            if (rootObj) {
                traverse(rootObj, null, 0)
            }
        }
        //TODO: determine if solver needs to run, if there are no open/active DOFs
    }

    public triggerUpdate(): void {
        this.solver.restart()
        this.stepsRemaining = this.defaultUpdateStepCount
    }

    public checkReady(): boolean {
        return this.solver.checkReady()
    }

    public step(): boolean {
        if (!this.solver.checkReady()) {
            return true
        }

        if (this.stepsRemaining > 0) {
            --this.stepsRemaining
            for (let i = 0; i < this.iterationsPerStep; i++) {
                this.solver.step()
            }
            if (this.stepsRemaining === 0) {
                // NOTE: These functions may reset stepsRemaining!
                // this.solver.dumpStats();
                this.updateObjectsStates()
                this.runPostConvergeCallbacks()
            }
        }

        return this.stepsRemaining > 0
    }

    private static readonly logger = loggerFactory.getLogger("scene")
}
